// src/hooks/useNotionData.js
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchDashboardSummary,
  fetchHealthPosts,
  fetchSportsPosts,
  fetchTodos,
} from '../lib/notion-api';

const CACHE = new Map();
const CACHE_TTL = 60 * 1000;

function getCached(key) {
  const entry = CACHE.get(key);
  if (entry && Date.now() - entry.ts < CACHE_TTL) return entry.data;
  CACHE.delete(key);
  return null;
}

function setCache(key, data) {
  CACHE.set(key, { data, ts: Date.now() });
}

/** 대시보드 통합 요약 */
export function useDashboard(autoRefresh = 300000) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (force = false, silent = false) => {
    if (!force) {
      const cached = getCached('dashboard');
      if (cached) { setData(cached); setLoading(false); return; }
    }
    if (!silent) setLoading(true);
    setError(null);
    try {
      const result = await fetchDashboardSummary();
      setData(result); setCache('dashboard', result);
    } catch (err) { if (!silent) setError(err.message); }
    finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    if (autoRefresh > 0) {
      const id = setInterval(() => load(true), autoRefresh);
      return () => clearInterval(id);
    }
  }, [load, autoRefresh]);

  return { data, loading, error, refresh: () => load(true), silentRefresh: () => load(true, true) };
}

/** 개별 DB 조회 (health / sports / todos) */
export function useNotionDB(dbType, params = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const paramsRef = useRef(params);

  const fetchFn = { health: fetchHealthPosts, sports: fetchSportsPosts, todos: fetchTodos }[dbType];

  const load = useCallback(async (silent = false) => {
    if (!fetchFn) { setError(`Unknown DB: ${dbType}`); return; }
    const key = `${dbType}:${JSON.stringify(paramsRef.current)}`;
    const cached = getCached(key);
    if (cached) { setData(cached); if (!silent) setLoading(false); return; }

    if (!silent) { setLoading(true); setError(null); }
    try {
      const result = await fetchFn(paramsRef.current);
      setData(result); setCache(key, result);
    } catch (err) { if (!silent) setError(err.message); }
    finally { if (!silent) setLoading(false); }
  }, [dbType, fetchFn]);

  useEffect(() => {
    paramsRef.current = params;
    load();
  }, [load, JSON.stringify(params)]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error, refresh: () => load(false), silentRefresh: () => load(true) };
}

/** 캐시 무효화 */
export function invalidateCache(prefix) {
  if (prefix) {
    for (const key of CACHE.keys()) { if (key.startsWith(prefix)) CACHE.delete(key); }
  } else { CACHE.clear(); }
}
